import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
 Calendar,
 Clock,
 MapPin,
 Users,
 Eye,
 Download,
 FileText,
 Loader2,
 MessageSquare,
 ArrowRight,
} from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { ClientEmptyState } from '../../components/client/guidance';
import { useClientBookings } from '../../services/bookingService';
import { clientInvoiceApi, useClientPendingInvoices } from '../../services/clientInvoiceService';
import { useToastContext } from '../../contexts/ToastContext';
import { DatePicker } from '../../components/client';
import { getStatusColor, formatStatusText, formatDate, formatTime } from '../../utils';
import { downloadInvoicePdfFromInvoiceData } from '../../utils/vendorInvoicePdf';

const BOOKINGS_SUBTITLE =
 'Track upcoming and past events, download invoices, and message vendors.';

const STATUS_PILLS = [
 { value: '', label: 'All' },
 { value: 'pending', label: 'Pending' },
 { value: 'confirmed', label: 'Confirmed' },
 { value: 'in_progress', label: 'In progress' },
 { value: 'completed', label: 'Completed' },
 { value: 'cancelled', label: 'Cancelled' },
];

const ClientBookings = () => {
 const queryClient = useQueryClient();
 const { success, error: showError } = useToastContext();
 const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
 const [filters, setFilters] = useState({
 status: '',
 start_date: '',
 end_date: '',
 });

 const cleanFilters = Object.fromEntries(
 Object.entries(filters).filter(
 ([_, value]) => value !== '' && value !== null && value !== undefined
 )
 );

 const { data: bookingsResponse, isLoading, error } = useClientBookings(cleanFilters);
 const { data: pendingInvoicesRes } = useClientPendingInvoices();
 const pendingInvoiceCount = pendingInvoicesRes?.data?.count ?? 0;

 const bookingsRaw =
 bookingsResponse?.data?.data || bookingsResponse?.data || bookingsResponse || [];

 const bookings = Array.isArray(bookingsRaw)
 ? [...bookingsRaw].sort((a, b) => {
 const dateA = new Date(a.created_at || a.event_date || 0);
 const dateB = new Date(b.created_at || b.event_date || 0);
 return dateB - dateA;
 })
 : [];

 const formatCurrency = (value) => {
 const num = parseFloat(value);
 if (isNaN(num) || num === null || num === undefined) return '0.00';
 return num.toFixed(2);
 };

 const calculateTotalDue = (booking) => {
 const subtotal = parseFloat(booking?.subtotal_amount) || 0;
 const platformFee = parseFloat(booking?.platform_fee) || 0;
 return subtotal + platformFee;
 };

 const handleFilterChange = (key, value) => {
 setFilters((prev) => ({ ...prev, [key]: value }));
 };

 const clearFilters = () => {
 setFilters({ status: '', start_date: '', end_date: '' });
 };

 const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
 setDownloadingInvoiceId(invoiceId);
 try {
 const res = await clientInvoiceApi.getOne(invoiceId);
 const inv = res?.data;
 if (!inv) {
 showError('Could not load invoice.');
 return;
 }
 const vendorProfile = inv.vendor || {};
 await downloadInvoicePdfFromInvoiceData(inv, vendorProfile);
 await clientInvoiceApi.markDownloaded(invoiceId);
 await queryClient.invalidateQueries({ queryKey: ['client-invoices-pending'] });
 await queryClient.invalidateQueries({ queryKey: ['bookings'] });
 success(`Invoice ${invoiceNumber || ''} downloaded.`.trim());
 } catch {
 showError('Could not download invoice. Please try again.');
 } finally {
 setDownloadingInvoiceId(null);
 }
 };

 const hasActiveFilters = Object.values(filters).some((f) => f);

 const layout = {
 title: 'My Bookings',
 subtitle: BOOKINGS_SUBTITLE,
 guideKey: 'bookings',
 };

 if (isLoading) {
 return (
 <ClientPageLayout {...layout}>
 <div className="space-y-5">
 <div className="product-panel animate-pulse p-5">
 <div className="h-5 w-28 bg-surface-muted rounded mb-4" />
 <div className="flex gap-2 mb-4">
 {[1, 2, 3, 4].map((i) => (
 <div key={i} className="h-9 w-20 bg-surface-muted rounded-full" />
 ))}
 </div>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
 <div className="h-10 bg-surface-muted rounded-md" />
 <div className="h-10 bg-surface-muted rounded-md" />
 </div>
 </div>
 {[1, 2].map((i) => (
 <div
 key={i}
 className="product-panel animate-pulse p-5"
 >
 <div className="h-5 w-40 bg-surface-muted rounded mb-4" />
 <div className="grid grid-cols-2 gap-3 mb-4">
 <div className="h-4 bg-surface-muted rounded" />
 <div className="h-4 bg-surface-muted rounded" />
 </div>
 <div className="h-10 w-28 bg-surface-muted rounded-md" />
 </div>
 ))}
 </div>
 </ClientPageLayout>
 );
 }

 if (error) {
 return (
 <ClientPageLayout {...layout}>
 <div className="rounded-lg border border-red-200/80 bg-red-50 p-8 text-center">
 <p className="font-semibold text-red-800 mb-1">Couldn’t load bookings</p>
 <p className="text-sm text-red-700/80">{error.message}</p>
 </div>
 </ClientPageLayout>
 );
 }

 return (
 <ClientPageLayout {...layout}>
 <div className="space-y-5">
 {pendingInvoiceCount > 0 && (
 <div className="rounded-lg border border-amber-200/80 bg-amber-50/90 px-4 py-3.5 sm:px-5 flex flex-col sm:flex-row sm:items-center gap-3">
 <div className="flex items-start gap-3 flex-1 min-w-0">
 <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-amber-200 bg-surface text-amber-800">
 <FileText className="h-4 w-4" />
 </div>
 <div>
 <p className="text-sm font-semibold text-amber-950">
 {pendingInvoiceCount} pending invoice{pendingInvoiceCount > 1 ? 's' : ''}
 </p>
 <p className="text-sm text-amber-900/80 mt-0.5">
 Download a PDF from the booking card below when you’re ready.
 </p>
 </div>
 </div>
 </div>
 )}

 {/* Filters */}
 <div className="product-filter-panel">
 <div className="mb-3 flex items-center justify-between gap-3">
 <h3 className="text-sm font-semibold text-ink">Filters</h3>
 {hasActiveFilters && (
 <button
 type="button"
 onClick={clearFilters}
 className="text-xs font-medium text-ink-secondary hover:text-ink"
 >
 Clear all
 </button>
 )}
 </div>

 <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Booking status">
 {STATUS_PILLS.map((pill) => {
 const active = filters.status === pill.value;
 return (
 <button
 key={pill.value || 'all'}
 type="button"
 role="tab"
 aria-selected={active}
 onClick={() => handleFilterChange('status', pill.value)}
 className={`product-chip ${active ? 'is-active' : ''}`}
 >
 {pill.label}
 </button>
 );
 })}
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
 <div>
 <label className="block text-xs font-medium text-ink-tertiary mb-1.5">
 From date
 </label>
 <DatePicker
 value={filters.start_date}
 onChange={(value) => handleFilterChange('start_date', value)}
 placeholder="Start date"
 className="w-full text-sm"
 />
 </div>
 <div>
 <label className="block text-xs font-medium text-ink-tertiary mb-1.5">
 To date
 </label>
 <DatePicker
 value={filters.end_date}
 onChange={(value) => handleFilterChange('end_date', value)}
 placeholder="End date"
 className="w-full text-sm"
 />
 </div>
 </div>
 </div>

 <div className="flex items-baseline justify-between gap-3 px-0.5">
 <h3 className="text-sm font-semibold text-ink">
 Your bookings
 <span className="ml-1.5 font-normal text-ink-tertiary">
 ({bookings?.length || 0})
 </span>
 </h3>
 </div>

 {!Array.isArray(bookings) || bookings.length === 0 ? (
 <ClientEmptyState
 icon={Calendar}
 title="No bookings found"
 description={
 hasActiveFilters
 ? 'Nothing matches these filters. Try clearing them or picking another status.'
 : 'When you book a vendor, your events will show up here.'
 }
 actionLabel={hasActiveFilters ? null : 'Browse vendors'}
 actionTo={hasActiveFilters ? undefined : '/browse-vendors'}
 tips={
 hasActiveFilters
 ? []
 : ['Browse vendors to find a service', 'Complete checkout to create a booking']
 }
 />
 ) : (
 <div className="space-y-3">
 {bookings.map((booking) => {
 const totalDue = calculateTotalDue(booking);
 const amountPaid = parseFloat(booking?.amount_paid) || 0;
 const remaining = Math.max(0, totalDue - amountPaid);
 const isFullyPaid = totalDue > 0 && amountPaid >= totalDue;
 const unread = Number(booking.unread_chat_count) || 0;

 return (
 <article
 key={booking.id}
 className="product-panel p-4 sm:p-5 transition-colors hover:border-ink"
 >
 <div className="flex flex-wrap items-center gap-2 mb-3">
 <h4 className="text-sm sm:text-base font-semibold tracking-tight text-ink">
 #{booking.booking_reference}
 </h4>
 <span
 className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor( booking.status )}`}
 >
 {formatStatusText(booking.status)}
 </span>
 {booking.client_invoice?.is_pending && (
 <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-900 border border-amber-200/80">
 Invoice pending
 </span>
 )}
 {unread > 0 && (
 <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">
 <MessageSquare className="h-3 w-3" />
 {unread > 99 ? '99+' : unread} new
 </span>
 )}
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mb-4">
 <div className="flex items-center gap-2 text-sm text-ink-secondary">
 <Calendar className="h-3.5 w-3.5 text-ink-tertiary shrink-0" />
 {formatDate(booking.event_date)}
 </div>
 <div className="flex items-center gap-2 text-sm text-ink-secondary">
 <Clock className="h-3.5 w-3.5 text-ink-tertiary shrink-0" />
 {formatTime(booking.start_time)} – {formatTime(booking.end_time)}
 </div>
 <div className="flex items-center gap-2 text-sm text-ink-secondary min-w-0">
 <MapPin className="h-3.5 w-3.5 text-ink-tertiary shrink-0" />
 <span className="truncate">{booking.event_location || 'Location TBD'}</span>
 </div>
 <div className="flex items-center gap-2 text-sm text-ink-secondary">
 <Users className="h-3.5 w-3.5 text-ink-tertiary shrink-0" />
 {booking.guest_count || 0} guests
 </div>
 </div>

 <div className="mb-4 pb-4 border-b border-border">
 <p className="text-sm font-semibold text-ink">
 {booking.vendor_service?.service_name || 'Service'}
 </p>
 <p className="text-xs text-ink-tertiary mt-0.5">
 {booking.vendor_service?.service_category?.category_name || 'Category'}
 {booking.vendor_service_package
 ? ` · ${booking.vendor_service_package.package_name}`
 : ''}
 </p>
 </div>

 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
 <div className="flex flex-wrap items-center gap-2 sm:gap-3">
 {(parseFloat(booking?.discount_amount) || 0) > 0 && (
 <span className="text-xs font-medium text-emerald-700">
 −${formatCurrency(booking.discount_amount)}
 {booking?.discount_code?.code ? ` (${booking.discount_code.code})` : ''}
 </span>
 )}
 <span className="text-base font-bold tabular-nums text-ink">
 ${formatCurrency(totalDue)}
 </span>
 <span className="text-xs text-ink-tertiary">
 Paid ${formatCurrency(amountPaid)}
 </span>
 {isFullyPaid ? (
 <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-200/80">
 Fully paid
 </span>
 ) : remaining > 0 ? (
 <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200/80">
 Balance ${formatCurrency(remaining)}
 </span>
 ) : null}
 </div>

 <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
 {booking.client_invoice && (
 <button
 type="button"
 onClick={() =>
 handleDownloadInvoice(
 booking.client_invoice.id,
 booking.client_invoice.invoice_number
 )
 }
 disabled={downloadingInvoiceId === booking.client_invoice.id}
 className="inline-flex items-center justify-center gap-1.5 rounded-product-md border border-border bg-surface px-3.5 py-2 text-xs font-medium text-ink hover:bg-surface-muted disabled:opacity-60"
 >
 {downloadingInvoiceId === booking.client_invoice.id ? (
 <Loader2 className="h-3.5 w-3.5 animate-spin" />
 ) : (
 <Download className="h-3.5 w-3.5" />
 )}
 Invoice
 </button>
 )}
 <Link
 to={`/bookings/${booking.id}`}
 className="relative inline-flex items-center justify-center gap-1.5 rounded-product-md bg-ink px-3.5 py-2 text-xs font-medium text-white hover:bg-black"
 >
 <Eye className="h-3.5 w-3.5" />
 View details
 <ArrowRight className="h-3.5 w-3.5 opacity-80" />
 {unread > 0 && (
 <span className="absolute -top-1.5 -right-1.5 min-w-[1.25rem] h-5 px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-semibold leading-none">
 {unread > 99 ? '99+' : unread}
 </span>
 )}
 </Link>
 </div>
 </div>
 </article>
 );
 })}
 </div>
 )}
 </div>
 </ClientPageLayout>
 );
};

export default ClientBookings;
